import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useLocation } from '@tanstack/react-router';
import type { RecordsRequestRow } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Kbd,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';
import type { ReactNode } from 'react';

import { ApiError } from '../api/client.ts';
import { recordReleasesQuery, recordsRequestsQuery } from '../api/records.ts';
import { can, sessionQuery } from '../api/session.ts';
import { screen, TableScroll } from '../layout/screen.tsx';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { FORMAT_WORDS, KIND_WORDS, stateTag } from './labels.ts';
import styles from './records.module.css';

// Records (docs/plans/phase2-records-data-rights.md §6): the data-subject request queue, the
// closed requests, and the records-release log at this facility.

export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function LoadFailed({
  error,
  fallback,
  onRetry,
}: {
  error: unknown;
  fallback: string;
  onRetry: () => void;
}) {
  return (
    <Notice
      tone="error"
      action={
        <Button dense onPress={onRetry}>
          Try again
        </Button>
      }
    >
      {errorText(error, fallback)}
    </Notice>
  );
}

function RecordsTabs() {
  const location = useLocation();
  const links = [
    { to: '/records', label: 'Open requests' },
    { to: '/records/closed', label: 'Closed requests' },
    { to: '/records/releases', label: 'Release log' },
  ] as const;
  return (
    <nav aria-label="Records" className={screen.tabs}>
      {links.map((l) => (
        <Link
          key={l.to}
          to={l.to}
          activeOptions={{ exact: true }}
          {...(location.pathname === l.to ? { 'aria-current': 'page' as const } : {})}
        >
          {l.label}
        </Link>
      ))}
    </nav>
  );
}

function Frame({ children }: { children: ReactNode }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  if (session === null || !can(session, 'records.requests.read'))
    return (
      <>
        <PageHeader title="Records" />
        <Notice tone="info">
          You don't have permission to see records requests. Ask an administrator if you need it.
        </Notice>
      </>
    );
  return (
    <div className={styles.page}>
      <PageHeader
        title="Records"
        description="Data-subject requests: access, correction, objection, restriction and deletion."
      />
      <div className={styles.toolbar}>
        <RecordsTabs />
        {can(session, 'records.requests.manage') ? (
          <p className={styles.meta}>
            To log a request, find the patient (<Kbd combo="mod+k" />) and choose Records request on
            their record.
          </p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function RequestsTable({ items, empty }: { items: RecordsRequestRow[]; empty: string }) {
  return (
    <TableScroll>
      <Table flat aria-label="Records requests">
        <TableHeader>
          <Column isRowHeader>Request</Column>
          <Column>Patient</Column>
          <Column>Kind</Column>
          <Column>Received</Column>
          <Column>Due</Column>
          <Column>State</Column>
        </TableHeader>
        <TableBody items={items} renderEmptyState={() => empty}>
          {(r) => {
            const tag = stateTag(r);
            return (
              <Row id={r.id}>
                <Cell>
                  <Link to="/records/requests/$requestId" params={{ requestId: r.id }}>
                    <span className="wodi-id">{r.number}</span>
                  </Link>
                </Cell>
                <Cell>
                  {r.patient.displayName} <span className="wodi-id">{r.patient.patientNumber}</span>
                </Cell>
                <Cell>{KIND_WORDS[r.kind]}</Cell>
                <Cell>{formatInstant(r.receivedAt)}</Cell>
                <Cell>{formatIsoDate(r.dueOn)}</Cell>
                <Cell>
                  <Tag state={tag.state}>{tag.word}</Tag>
                </Cell>
              </Row>
            );
          }}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function RequestsView({ state }: { state: 'open' | 'closed' }) {
  const requests = useQuery(recordsRequestsQuery(state));
  return (
    <Frame>
      <Section
        title={state === 'open' ? 'Open requests' : 'Closed requests'}
        description={
          state === 'open'
            ? 'By due date. Due dates are provisional until the legal review.'
            : 'Newest first.'
        }
      >
        {requests.isPending ? <LoadingBlock label="Loading requests" rows={4} /> : null}
        {requests.isError ? (
          <LoadFailed
            error={requests.error}
            fallback="Requests couldn't be loaded."
            onRetry={() => void requests.refetch()}
          />
        ) : null}
        {requests.isSuccess ? (
          <RequestsTable
            items={requests.data.items}
            empty={
              state === 'open'
                ? "No open requests. Log one from the patient's record."
                : 'No closed requests yet.'
            }
          />
        ) : null}
      </Section>
    </Frame>
  );
}

export function OpenRequestsPage() {
  return <RequestsView state="open" />;
}

export function ClosedRequestsPage() {
  return <RequestsView state="closed" />;
}

export function ReleaseLogPage() {
  const releases = useQuery(recordReleasesQuery);
  return (
    <Frame>
      <Section
        title="Release log"
        description="Every copy of a record released at this facility: to whom, why, and a fingerprint of what was given."
      >
        {releases.isPending ? <LoadingBlock label="Loading the release log" rows={4} /> : null}
        {releases.isError ? (
          <LoadFailed
            error={releases.error}
            fallback="The release log couldn't be loaded."
            onRetry={() => void releases.refetch()}
          />
        ) : null}
        {releases.isSuccess ? (
          <TableScroll>
            <Table flat aria-label="Release log">
              <TableHeader>
                <Column isRowHeader>Released</Column>
                <Column>Patient</Column>
                <Column>Request</Column>
                <Column>Format</Column>
                <Column>To</Column>
                <Column>By</Column>
                <Column>Fingerprint</Column>
              </TableHeader>
              <TableBody
                items={releases.data.items}
                renderEmptyState={() =>
                  'Nothing released yet. Records are prepared from a verified access request.'
                }
              >
                {(r) => (
                  <Row id={r.id}>
                    <Cell>{formatInstant(r.releasedAt)}</Cell>
                    <Cell>
                      {r.patient.displayName}{' '}
                      <span className="wodi-id">{r.patient.patientNumber}</span>
                    </Cell>
                    <Cell>
                      <Link to="/records/requests/$requestId" params={{ requestId: r.requestId }}>
                        <span className="wodi-id">{r.requestNumber}</span>
                      </Link>
                    </Cell>
                    <Cell>{FORMAT_WORDS[r.format]}</Cell>
                    <Cell>{r.recipient}</Cell>
                    <Cell>{r.releasedBy.displayName}</Cell>
                    <Cell>
                      <span className="wodi-id" title={r.contentHash}>
                        {r.contentHash.slice(0, 12)}
                      </span>
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        ) : null}
      </Section>
    </Frame>
  );
}
